import * as React from 'react';

import {
  ButtonBase,
  type ButtonBaseActions,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { useDropZone, useFileDialog } from '@realadvisor/hooks';
import { Upload } from 'lucide-react';
import * as ReactDOM from 'react-dom';

import { useLocale } from '@shared/hooks/locale';

type DropZoneProps = {
  onDrop: (files: File[]) => unknown;
  accept?: string;
  acceptLabel?: string;
};

const Portal = ({ children }: { children: React.ReactNode }) => {
  if (document.body == null) {
    return null;
  } else {
    return ReactDOM.createPortal(children, document.body);
  }
};

export const FullScreenDropZone = (
  props: Pick<DropZoneProps, 'accept' | 'acceptLabel'> & {
    disabled?: boolean;
    onDrop: (files: File[], accepted: boolean) => unknown;
  },
) => {
  const { t } = useLocale();
  const theme = useTheme();
  const { disabled, onDrop } = props;
  const [allAccepted, setAllaccepted] = React.useState(false);
  const targetRef = React.useRef(document.body);
  const onDropHandler = React.useCallback(
    (files: File[]) => {
      if (disabled) {
        return;
      }

      onDrop(files, allAccepted);
    },
    [disabled, onDrop, allAccepted],
  );
  const dragOver = useDropZone({
    targetRef,
    accept: props.accept ?? 'image/*',
    onDragEnter: (_acceptedItems, rejectedItems) =>
      setAllaccepted(rejectedItems.length === 0),
    onDrop: onDropHandler,
  });

  const notAllowedMessage =
    props.accept == null
      ? t('Only images are allowed')
      : t('Only {{accept}} are allowed', {
          accept: props.acceptLabel ?? props.accept,
        });

  return (
    <Portal>
      <div
        css={{
          position: 'fixed',
          zIndex: 10000,
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,

          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',

          pointerEvents: 'none',
          border: `8px solid ${
            allAccepted
              ? (theme.vars ?? theme).palette.success.main
              : (theme.vars ?? theme).palette.warning.main
          }`,
          backgroundColor: 'rgba(0, 0, 0, 0.54)',
          color: theme.palette.common.white,
          opacity: dragOver && !props.disabled ? 1 : 0,
          transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Upload size={48} />
        <Typography variant="h6">
          {allAccepted ? t('Drop to upload') : notAllowedMessage}
        </Typography>
      </div>
    </Portal>
  );
};

export const DropZoneButton = ({
  onDrop,
  accept = '*',
  multiple = true,
  isLoading = false,
  disabled = false,
}: DropZoneProps & {
  multiple?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useLocale();
  const targetRef = React.useRef<HTMLButtonElement | null>(null);
  const actionsRef = React.useRef<ButtonBaseActions | null>(null);
  const isDisabled = disabled || isLoading;
  const dragOver = useDropZone({
    targetRef,
    accept,
    onDragEnter: () => {
      if (actionsRef.current) {
        actionsRef.current.focusVisible();
      }
    },
    onDragLeave: () => {
      if (targetRef.current) {
        targetRef.current.blur();
      }
    },
    onDrop: files => {
      if (!isDisabled && files.length > 0) {
        onDrop(files);
      }
    },
  });

  const openFileDialog = useFileDialog({
    accept,
    onChange: onDrop,
    multiple,
  });

  return (
    <ButtonBase
      ref={targetRef}
      focusRipple={true}
      disabled={isDisabled}
      action={actions => {
        actionsRef.current = actions;
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        height: 80,
        width: '100%',
        p: '8px 16px',
        color: 'primary.main',
        opacity: disabled && !isLoading ? 0.5 : 1,
      }}
      onClick={openFileDialog}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row',
          gap: 8,
          borderRadius: 8,
          flex: 1,
          border: '1px dashed currentColor',
          transition: 'all 0.15s ease',
        }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={18} color="inherit" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('Uploading...')}
            </Typography>
          </>
        ) : (
          <>
            <Upload size={20} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {dragOver ? t('Drop to upload') : t('Drag or click to upload')}
            </Typography>
          </>
        )}
      </div>
    </ButtonBase>
  );
};
